import React, { memo, useMemo } from 'react';
import { Radio } from 'lucide-react';

/**
 * Generate realistic intraday price movement for a stock
 */
const generateRealisticStockChart = (stock) => {
  const currentPrice = parseFloat(stock.price);
  const changePercent = parseFloat(stock.percent);
  const ticker = stock.ticker;
  
  const seed = ticker.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const seededRandom = (index) => {
    const x = Math.sin(seed + index * 1000) * 10000;
    return x - Math.floor(x);
  };
  
  const volatilityMap = {
    'TSLA': 0.3, 'NVDA': 0.25, 'META': 0.22, 'AMZN': 0.2,
    'GOOGL': 0.18, 'AAPL': 0.15, 'MSFT': 0.12, 'JPM': 0.1,
    'V': 0.08, 'WMT': 0.06, 'SPX': 0.05
  };
  
  const baseVolatility = volatilityMap[ticker] || 0.15;
  const points = 12;
  const chartData = [];
  const openingPrice = currentPrice / (1 + (changePercent / 100));
  
  for (let i = 0; i < points; i++) {
    const progress = i / (points - 1);
    const noise = (seededRandom(i) - 0.5) * baseVolatility * (currentPrice * 0.005);
    const easedProgress = progress < 0.5 
      ? 2 * progress * progress 
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    
    let currentValue = openingPrice + (currentPrice - openingPrice) * easedProgress + noise;
    if (i === points - 1) currentValue = currentPrice;
    
    chartData.push({ price: currentValue });
  }
  
  const prices = chartData.map(d => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;
  const isSmallChange = Math.abs(changePercent) < 1.5;
  const minHeight = isSmallChange ? 30 : 10;
  const maxHeight = isSmallChange ? 70 : 90;
  
  return chartData.map(point => ({
    price: point.price,
    height: priceRange === 0 ? 50 : minHeight + ((point.price - minPrice) / priceRange) * (maxHeight - minHeight)
  }));
};

/**
 * Stock Card Component with Realistic Chart
 */
export const StockCard = memo(({ stock, onListen }) => {
  const chartData = useMemo(() => generateRealisticStockChart(stock), [stock.ticker, stock.price, stock.percent]);
  
  return (
    <div style={{ flexShrink: 0, width: '200px' }}>
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.31)', 
        backdropFilter: 'blur(20px)', 
        borderRadius: '16px', 
        padding: '5px', 
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', 
        border: '1px solid rgba(255, 255, 255, 0.93)', 
        transition: 'transform 0.3s ease', 
        cursor: 'pointer' 
      }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '16px', 
          border: '1px solid rgba(213, 213, 213, 0.5)' 
        }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', marginBottom: '2px' }}>
              {stock.ticker}
            </div>
            <div style={{ fontSize: '10px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stock.name}
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
              ${stock.price}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: stock.isUp ? '#10b981' : '#ef4444' }}>
                {stock.change}
              </span>
              <span style={{ 
                fontSize: '11px', 
                fontWeight: '500', 
                color: stock.isUp ? '#10b981' : '#ef4444', 
                background: stock.isUp ? '#d1fae5' : '#fee2e2', 
                padding: '2px 6px', 
                borderRadius: '4px' 
              }}>
                {stock.percent}
              </span>
            </div>
          </div>

          <div style={{ height: '40px', marginBottom: '12px', display: 'flex', alignItems: 'flex-end', gap: '2px' }}>
            {chartData.map((point, i) => (
              <div 
                key={i}
                style={{ 
                  flex: 1, 
                  height: `${point.height}%`, 
                  background: stock.isUp ? '#10b981' : '#ef4444',
                  borderRadius: '2px 2px 0 0',
                  opacity: 0.6 + (i / 24),
                  transition: 'height 0.3s ease',
                  willChange: 'height'
                }}
                title={`$${point.price.toFixed(2)}`}
              />
            ))}
          </div>

          <button
            onClick={onListen}
            style={{ 
              width: '100%', 
              padding: '10px', 
              background: '#070707ff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '20px', 
              fontSize: '11px', 
              fontWeight: '500', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '6px' 
            }}
          >
            <Radio size={14} />
           24/7 - Station
          </button>
        </div>
      </div>
    </div>
  );
});

StockCard.displayName = 'StockCard';

/**
 * Skeleton Loading State
 */
export const SkeletonStockCard = () => (
  <div style={{ flexShrink: 0, width: '200px' }}>
    <div style={{ 
      background: 'rgba(255, 255, 255, 0.31)', 
      backdropFilter: 'blur(20px)', 
      borderRadius: '16px', 
      padding: '5px', 
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', 
      border: '1px solid rgba(255, 255, 255, 0.93)' 
    }}>
      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid rgba(213, 213, 213, 0.5)' }}>
        {/* Skeleton Ticker */}
        <div style={{ 
          height: '20px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          marginBottom: '4px',
          width: '50%'
        }} />
        <div style={{ 
          height: '12px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          marginBottom: '12px',
          width: '70%'
        }} />
        
        {/* Skeleton Price */}
        <div style={{ 
          height: '24px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          marginBottom: '8px',
          width: '60%'
        }} />
        
        {/* Skeleton Chart */}
        <div style={{ height: '40px', marginBottom: '12px', display: 'flex', alignItems: 'flex-end', gap: '2px' }}>
          {[30, 45, 35, 50, 40, 55, 45, 60, 50, 65, 55, 70].map((height, i) => (
            <div 
              key={i}
              style={{ 
                flex: 1, 
                height: `${height}%`, 
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
                borderRadius: '2px 2px 0 0',
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
        
        {/* Skeleton Button */}
        <div style={{ 
          height: '36px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '10px'
        }} />
      </div>
    </div>
    
    <style>{`
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);